import { observer } from "mobx-react";
import { useStore } from "utils/useStore";
import { LLMClassifierBuilderStore } from "../Stores/LLMClassifierBuilderStore";
import {
    ConditionPrimitive as ConditionPrimitiveClass,
    ConditionPropertyType,
} from "models/LLMs";
import { ConditionPrimitive } from "./LogicChainBuilder/Conditions/ConditionPrimitive";
import React from "react";
import { Divider, Grid, Link, Typography } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import { ConditionExpressionType } from "models/LLMs/LogicChaining/Conditions/ConditionExpressionType";
import { Add } from "@mui/icons-material";
import AcxChip from "components/UI/AcxChip";
import { v4 } from "uuid";
import { ChainSourceStore } from "../Stores/ChainSourceStore";
import { LLMClassifierBuilderStepperStore } from "../Stores/LLMClassifierBuilderStepperStore";

/**
 * Metadata filters for this model. Run conditions are stored in the same list,
 * because both gate execution the same way, but they are owned by the Chain
 * source section on the builder step -- editing them here would mean the same
 * gate is configurable in two places, so they are shown read-only and only the
 * conditions this step is responsible for are editable: metadata, extended
 * metadata, and legacy model conditions that do not yet belong to a parent
 * relationship.
 */
export const LLMClassifierExecuteConditions = observer(() => {
    const store = useStore(LLMClassifierBuilderStore);
    const chainSourceStore = useStore(ChainSourceStore);
    const stepper = useStore(LLMClassifierBuilderStepperStore);
    const builder = store.classifier.implementation.configurationBuilder;

    const isChainOwned = (condition: { id: string }) =>
        chainSourceStore.getRunCondition(condition.id) !== undefined;

    const metadataConditions = builder.executeConditions.filter(
        (condition): condition is ConditionPrimitiveClass =>
            condition instanceof ConditionPrimitiveClass &&
            !isChainOwned(condition),
    );
    const chainConditionGroups = chainSourceStore.parents
        .map((parent) => ({
            parent,
            conditions: builder.executeConditions.filter(
                (condition): condition is ConditionPrimitiveClass =>
                    condition instanceof ConditionPrimitiveClass &&
                    chainSourceStore.getRunCondition(condition.id)?.parentId ===
                        parent.id,
            ),
        }))
        .filter((group) => group.conditions.length > 0);

    // Until the chain loads there are no parents to own anything, so every run
    // condition would list as an editable row and could be removed from here.
    if (chainSourceStore.isLoadingChain || chainSourceStore.hasChainLoadFailed) {
        return (
            <Typography
                sx={(theme) => ({
                    color: chainSourceStore.hasChainLoadFailed
                        ? theme.palette.error.main
                        : theme.palette.gray[500],
                    fontSize: "13px",
                })}
            >
                {chainSourceStore.hasChainLoadFailed
                    ? "Conditions couldn't be loaded. Reload the page to edit them."
                    : "Loading conditions..."}
            </Typography>
        );
    }

    return (
        <Grid container direction="column" rowGap={1} flexWrap="nowrap">
            {metadataConditions.map((c, idx) => (
                <React.Fragment key={c.id}>
                    <Grid item>
                        <ConditionPrimitive condition={c}>
                            <ConditionPrimitive.MetadataPropertyName
                                excludeModelVariables
                            />
                            <ConditionPrimitive.ExpressionType />
                            <ConditionPrimitive.Value />
                            <ConditionPrimitive.IsCaseSensitive />
                            <ConditionPrimitive.Remove
                                onRemove={(condition) =>
                                    builder.removeExecuteCondition(condition)
                                }
                            />
                        </ConditionPrimitive>
                    </Grid>
                    {idx !== metadataConditions.length - 1 && (
                        <Grid item>
                            <AcxChip
                                color="lightBlue"
                                label="AND"
                                size="small"
                            />
                        </Grid>
                    )}
                </React.Fragment>
            ))}
            <Grid item>
                <Grid item marginTop={2}>
                    <AcxButton
                        color="white"
                        startIcon={<Add />}
                        onClick={() =>
                            builder.addExecuteCondition(
                                new ConditionPrimitiveClass({
                                    id: v4(),
                                    propertyName: "",
                                    operation: ConditionExpressionType.Equal,
                                    value: "",
                                    propertyType:
                                        ConditionPropertyType.Conversation,
                                }),
                            )
                        }
                        sx={(theme) => ({
                            color: theme.palette.primary.main,
                        })}
                    >
                        Condition
                    </AcxButton>
                </Grid>
            </Grid>
            {chainConditionGroups.length > 0 && (
                <>
                    <Grid item marginBlock={1}>
                        {metadataConditions.length > 0 ? (
                            <Divider
                                textAlign="left"
                                sx={{
                                    "&::before": { display: "none" },
                                    "& .MuiDivider-wrapper": { paddingLeft: 0 },
                                }}
                            >
                                <AcxChip
                                    color="lightBlue"
                                    label="AND"
                                    size="small"
                                />
                            </Divider>
                        ) : (
                            <Divider />
                        )}
                    </Grid>
                    <Grid item>
                        <Typography
                            sx={(theme) => ({
                                color: theme.palette.gray[500],
                                fontSize: "12px",
                            })}
                        >
                            Run conditions from Dependencies must also pass.{" "}
                            <Link
                                component="button"
                                type="button"
                                underline="hover"
                                sx={{
                                    fontSize: "inherit",
                                    verticalAlign: "baseline",
                                }}
                                onClick={() => {
                                    chainSourceStore.requestReveal();
                                    stepper.setCurrentWizardStep("builder");
                                }}
                            >
                                Edit them on the Builder step
                            </Link>
                        </Typography>
                    </Grid>
                    {chainConditionGroups.map(
                        ({ parent, conditions }, groupIdx) => (
                            <React.Fragment key={parent.id}>
                                {groupIdx > 0 && (
                                    <Grid item>
                                        <AcxChip
                                            color="lightBlue"
                                            label="AND"
                                            size="small"
                                        />
                                    </Grid>
                                )}
                                <Grid item>
                                    <Typography
                                        sx={(theme) => ({
                                            color: theme.palette.gray[700],
                                            fontSize: "13px",
                                            fontWeight: 600,
                                        })}
                                    >
                                        {parent.name}
                                    </Typography>
                                </Grid>
                                {conditions.map((c, idx) => (
                                    <React.Fragment key={c.id}>
                                        {idx > 0 && (
                                            <Grid item>
                                                <AcxChip
                                                    color="lightBlue"
                                                    label="AND"
                                                    size="small"
                                                />
                                            </Grid>
                                        )}
                                        <Grid item>
                                            <ConditionPrimitive condition={c}>
                                                <ConditionPrimitive.MetadataPropertyName
                                                    disabled
                                                />
                                                <ConditionPrimitive.ExpressionType
                                                    disabled
                                                />
                                                <ConditionPrimitive.Value
                                                    disabled
                                                />
                                                <ConditionPrimitive.IsCaseSensitive
                                                    disabled
                                                />
                                            </ConditionPrimitive>
                                        </Grid>
                                    </React.Fragment>
                                ))}
                            </React.Fragment>
                        ),
                    )}
                </>
            )}
        </Grid>
    );
});
